/* ==========================================================================
   BRICKSMART — "X customers have this in their cart"
   --------------------------------------------------------------------------
   Companion to stock.js and built the same way, on purpose: one place decides
   how the line looks, and two interchangeable providers decide what number
   goes in it.

       window.BM_SOCIAL_CONFIG.mode = "live"   ->  real cart counts  (PRODUCTION)
       window.BM_SOCIAL_CONFIG.mode = "demo"   ->  placeholder       (PRE-LAUNCH ONLY)

   ------------------------------------------------------------------ FOR DEV
   DEMO MODE SHOWS A NUMBER THAT IS NOT REAL. It is a seeded placeholder so the
   component can be styled, positioned and QA'd before the data exists. It must
   be switched to "live" before this is shown to customers at scale.

   Wiring live:
     There is no Shopify Storefront query for "how many open carts contain this
     product" — carts are not queryable that way. This needs one small backend
     endpoint of your own. Set:

       window.BM_SOCIAL_CONFIG.mode     = "live";
       window.BM_SOCIAL_CONFIG.endpoint = "https://api.yoursite.com/cart-count";

     fetchLive() will GET  <endpoint>?pid=<product-id>  and expects JSON:

       { "count": 12 }              // number of open carts containing this set

     Anything else (non-200, malformed, count == null) hides the line rather
     than falling back to a made-up number. Failing silent-and-empty is the
     correct behaviour here: no claim is better than an invented one.

     Suggested definition on your side: distinct carts updated in the last
     24–48h that contain this product and have not converted. Whatever you
     choose, keep it stable — the number should not swing wildly on refresh.
   ========================================================================== */

(function () {
  "use strict";

  var CFG   = window.BM_SOCIAL_CONFIG || {};
  var MODE  = CFG.mode || "demo";
  var MIN   = CFG.min != null ? CFG.min : 5;
  var MAX   = CFG.max != null ? CFG.max : 23;
  var REFRESH = CFG.refreshMs || 120000;          // live poll interval
  var LS_KEY  = "bm_social_v1";

  /* ---------------------------------------------------------------- render */

  function els() {
    return [].slice.call(document.querySelectorAll("[data-cartsocial]"));
  }

  /* The only place this line's appearance is decided. Same in both modes. */
  function paint(n) {
    els().forEach(function (el) {
      if (n == null || !isFinite(n) || n < MIN) {
        el.style.visibility = "hidden";           // no number -> no claim
        return;
      }
      var slot = el.querySelector("#social-n") || el.querySelector("[data-social-n]");
      if (slot) slot.textContent = String(n);
      el.style.visibility = "visible";
    });
  }

  /* ------------------------------------------------- provider: LIVE (prod) */

  function fetchLive(pid) {
    var url = CFG.endpoint;
    if (!url || !pid) return Promise.resolve({ count: null });
    var sep = url.indexOf("?") > -1 ? "&" : "?";
    return fetch(url + sep + "pid=" + encodeURIComponent(pid), {
      method: "GET",
      headers: { "Accept": "application/json" },
      credentials: "omit"
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var n = d && d.count;
        return { count: (typeof n === "number" && isFinite(n)) ? n : null };
      })
      .catch(function () { return { count: null }; });   // hide, never invent
  }

  /* ------------------------------------------- provider: DEMO (pre-launch) */
  /* Seeded, so one shopper sees a consistent number for a given set instead of
     a fresh random on every reload — a figure that jumps on refresh reads as
     fake immediately. The seed rolls once a day and drifts by a unit or two
     within a session, which is roughly how a real count behaves.            */

  function seed(str) {                            // FNV-1a -> 32-bit int
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function rng(s) {                               // mulberry32
    var a = s >>> 0;
    var f = function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    /* Discard the first few draws. Straight out of the gate mulberry32
       correlates across similar seeds, which made the catalogue cluster at the
       top of the range instead of spreading across it. */
    f(); f(); f();
    return f;
  }

  function dayBucket() {
    return Math.floor(Date.now() / 86400000);     // rolls at UTC midnight
  }

  function clamp(n) {
    return Math.max(MIN, Math.min(MAX, n));
  }

  function readStore() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch (e) { return {}; }
  }
  function writeStore(o) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(o)); } catch (e) {}
  }

  /* Drift: a small ± move every few minutes so the line isn't frozen, bounded
     so it never wanders outside MIN..MAX or lurches by more than one. */
  var DRIFT_MIN_MS = 180000;                      // 3 min
  var DRIFT_MAX_MS = 480000;                      // 8 min

  function fetchDemo(pid) {
    var now   = Date.now();
    var day   = dayBucket();
    var store = readStore();
    var rec   = store[pid];

    if (!rec || rec.day !== day || typeof rec.count !== "number") {
      var base = rng(seed(pid + "|" + day));
      rec = {
        day: day,
        count: clamp(MIN + Math.floor(base() * (MAX - MIN + 1))),
        next: now + DRIFT_MIN_MS + Math.floor(base() * (DRIFT_MAX_MS - DRIFT_MIN_MS))
      };
    } else if (now >= (rec.next || 0)) {
      var r = rng(seed(pid + "|" + day + "|" + now));
      rec.count = clamp(rec.count + (r() < 0.5 ? -1 : 1));
      rec.next  = now + DRIFT_MIN_MS + Math.floor(r() * (DRIFT_MAX_MS - DRIFT_MIN_MS));
    }

    rec.count = clamp(rec.count);
    store[pid] = rec;
    writeStore(store);
    return Promise.resolve({ count: rec.count, next: rec.next });
  }

  /* ------------------------------------------------------------ controller */

  var timer = null;

  function tick(pid) {
    var get = MODE === "live" ? fetchLive : fetchDemo;
    return get(pid).then(function (res) {
      paint(res.count);
      clearTimeout(timer);
      var wait = MODE === "live"
        ? REFRESH
        : Math.max(30000, (res.next || Date.now() + REFRESH) - Date.now());
      timer = setTimeout(function () { tick(pid); }, wait);
      return res;
    });
  }

  function init() {
    if (!els().length) return;
    var pid = (window.BM_PAGE && window.BM_PAGE.pid) || null;
    if (!pid) { paint(null); return; }

    if (MODE !== "live") {
      try {
        console.warn(
          "[BM social] PLACEHOLDER MODE — the number in the \"customers have this " +
          "in their cart\" line is generated, not a real cart count. Set " +
          "BM_SOCIAL_CONFIG.mode = 'live' and point BM_SOCIAL_CONFIG.endpoint at " +
          "your cart-count API (see the header of social.js) before this runs at scale."
        );
      } catch (e) {}
    }

    tick(pid);
    document.addEventListener("visibilitychange", function () {
      if (!document.hidden) tick(pid);
    });
  }

  /* public surface — handy for QA: BMSocial.set(12) / BMSocial.refresh() */
  window.BMSocial = {
    mode: MODE,
    refresh: function () { return tick((window.BM_PAGE || {}).pid); },
    set: function (n) { paint(n); },              // preview any value instantly
    fetchLive: fetchLive
  };

  /* mode "off" — render nothing and hide the container outright.
     NOTE: every branch below used `MODE === "live" ? fetchLive : fetchDemo`, so
     ANY value that wasn't "live" fell through to the placeholder generator —
     "off" silently behaved exactly like "demo". This is the explicit stop. */
  function hideAll() {
    try { els().forEach(function (el) { el.style.display = "none"; }); } catch (_e) {}
    try { console.info("[BM social] mode \'off\' — hidden, no number rendered."); } catch (_e) {}
  }
  function boot() { if (MODE === "off") { hideAll(); return; } init(); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

/*bm-tail-patched*/
